/* eslint-disable react/button-has-type */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import * as AppPropTypes from '../../lib/PropTypes';
import Label from '../partials/Label';

import styles from '../../../styles/buttons/button.scss';

const propTypes = {
    type: PropTypes.string,
    href: PropTypes.string,
    external: PropTypes.bool,
    direct: PropTypes.bool,
    target: PropTypes.string,
    label: AppPropTypes.label,
    children: AppPropTypes.label,
    icon: PropTypes.node,
    color: AppPropTypes.color,
    invertedColor: AppPropTypes.color,
    reverse: PropTypes.bool,
    centered: PropTypes.bool,
    highlighted: PropTypes.bool,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    disableOnLoading: PropTypes.bool,
    className: PropTypes.string,
    iconClassName: PropTypes.string,
    labelClassName: PropTypes.string,
    backgrounds: PropTypes.arrayOf(PropTypes.func),
    onClick: PropTypes.func,
};

const defaultProps = {
    type: 'button',
    href: null,
    external: false,
    direct: false,
    target: '_blank',
    label: null,
    children: null,
    icon: null,
    color: '#FFF',
    invertedColor: '#25786f',
    centered: false,
    highlighted: false,
    reverse: false,
    disabled: false,
    loading: false,
    disableOnLoading: true,
    iconClassName: null,
    labelClassName: null,
    backgrounds: [],
    onClick: null,
    className: null,
};

const Button = ({
    type,
    href,
    external,
    direct,
    target,
    label,
    children,
    icon,
    color,
    invertedColor,
    reverse,
    centered,
    highlighted,
    disabled,
    loading,
    disableOnLoading,
    iconClassName,
    labelClassName,
    backgrounds,
    onClick,
    className,
}) => {
    const finalLabel = label || children;
    const Background = backgrounds[0] || null;
    const hasIcon = icon !== null;
    const textStyle = reverse ? { color: invertedColor } : { color };
    const backgroundStyle = reverse ? { color } : { color: invertedColor };
    const text = <Label>{finalLabel}</Label>;

    const content = (
        <>
            {hasIcon ? (
                <>
                    <span
                        className={classNames([
                            styles.icon,
                            {
                                [iconClassName]: iconClassName !== null,
                            },
                        ])}
                        style={!highlighted ? textStyle : null}
                    >
                        {icon}
                    </span>
                    {text !== null ? (
                        <span
                            className={classNames([
                                styles.label,
                                {
                                    [labelClassName]: labelClassName !== null,
                                },
                            ])}
                            style={!highlighted ? textStyle : null}
                        >
                            {text}
                        </span>
                    ) : null}
                </>
            ) : null}
            {!hasIcon ? (
                <span className={styles.label} style={!highlighted ? textStyle : null}>
                    {text}
                </span>
            ) : null}
            {Background ? (
                <Background
                    className={styles.background}
                    style={!highlighted ? backgroundStyle : null}
                />
            ) : null}
        </>
    );

    const buttonClassNames = classNames([
        styles.container,
        {
            [styles.withIcon]: hasIcon,
            [styles.withText]: text !== null,
            [styles.isLink]: href !== null,
            [styles.disabled]: disabled,
            [styles.isLoading]: loading,
            [styles.centered]: centered,
            [styles.highlighted]: highlighted,
            [className]: className !== null,
        },
    ]);
    if (href !== null) {
        return external || direct ? (
            <a
                href={href}
                className={buttonClassNames}
                onClick={onClick}
                target={external ? target : null}
            >
                {content}
            </a>
        ) : (
            <Link to={href} className={buttonClassNames} onClick={onClick}>
                {content}
            </Link>
        );
    }

    return (
        <button
            type={type}
            className={buttonClassNames}
            onClick={onClick}
            disabled={disabled || (disableOnLoading && loading)}
        >
            {content}
        </button>
    );
};

Button.propTypes = propTypes;
Button.defaultProps = defaultProps;

export default Button;
